import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { preContractsApi } from 'api';
import { getPreContractPreviewUrl } from 'api/preContractsApi';
import LanguagePopover from 'layouts/layout-parts/popovers/LanguagePopover';
import { useTranslation } from 'react-i18next';

/**
 * Public, token-gated page where an employee:
 *   1. Sees the pre-filled pre-contract PDF.
 *   2. Ticks an explicit "I have read and accept" checkbox.
 *   3. Types their full name + draws their signature on the canvas.
 *   4. Submits. The backend embeds the signature into a new PDF, uploads it
 *      to S3, and marks the magic-link token as consumed.
 *
 * Mobile-first: the PDF preview collapses to a smaller height on phones; the
 * canvas uses pointer events so it works with both finger and stylus.
 */
const PreContractSignPage: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const tenant = params.get('tenant') ?? '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<{ employee: { firstName: string; lastName: string }; snapshot: any } | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  const previewUrl = useMemo(
    () => (token && tenant ? getPreContractPreviewUrl(token, tenant) : ''),
    [token, tenant],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await preContractsApi.getByToken(token, tenant);
        const payload = res.data as any;
        setInfo(payload);
        setFullName(`${payload.employee.firstName} ${payload.employee.lastName}`.trim());
      } catch (e: any) {
        setError(
          e?.response?.data?.message ??
            t('preContract.public.invalidLink', 'Questo link non è più valido.'),
        );
      } finally {
        setLoading(false);
      }
    };
    if (!token || !tenant) {
      setError(t('preContract.public.missingParams', 'Link non valido.'));
      setLoading(false);
    } else {
      void load();
    }
  }, [token, tenant, t]);

  // ---------- Signature canvas ----------
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (canvas: HTMLCanvasElement, e: React.PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    c.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = getPos(c, e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(c, e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };
  const handlePointerUp = () => {
    drawing.current = false;
  };
  const clearSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    const c = canvasRef.current;
    if (!agreed || !hasSignature || !fullName.trim() || !c) return;
    const dataUrl = c.toDataURL('image/png');
    setSubmitting(true);
    try {
      const res = await preContractsApi.signByToken(token, tenant, {
        signatureDataUrl: dataUrl,
        fullName: fullName.trim(),
        agreed: true,
      } as any);
      setSignedUrl((res.data as any).downloadUrl);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ??
          t('preContract.public.signError', 'Errore durante la firma. Riprova più tardi.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' minHeight='100vh'>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth='sm' sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant='h5' fontWeight={700} gutterBottom>
            {t('preContract.public.errorTitle', 'Link non disponibile')}
          </Typography>
          <Typography color='text.secondary'>{error}</Typography>
        </Box>
      </Container>
    );
  }

  if (signedUrl) {
    return (
      <Container maxWidth='sm' sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <CheckCircleOutlineIcon color='success' sx={{ fontSize: 64 }} />
          <Typography variant='h5' fontWeight={700} mt={2} gutterBottom>
            {t('preContract.public.successTitle', 'Pre-contratto firmato!')}
          </Typography>
          <Typography color='text.secondary' mb={3}>
            {t(
              'preContract.public.successDescription',
              'Abbiamo registrato la tua firma. Puoi scaricare una copia del documento firmato qui sotto.',
            )}
          </Typography>
          <Button
            variant='contained'
            size='large'
            href={signedUrl}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('preContract.public.downloadSigned', 'Scarica copia firmata')}
          </Button>
        </Box>
      </Container>
    );
  }

  const canSubmit = agreed && hasSignature && fullName.trim().length > 1 && !submitting;

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pb: 6 }}>
      <Box sx={{ bgcolor: '#0F172A', color: 'white', px: { xs: 2, md: 4 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction='row' alignItems='center' gap={1}>
          <DescriptionIcon />
          <Typography variant='h6' fontWeight={600}>
            {t('preContract.public.headerTitle', 'Pre-contratto')}
          </Typography>
        </Stack>
        <LanguagePopover disableScrollLock />
      </Box>

      <Container maxWidth='md' sx={{ pt: { xs: 2, md: 4 } }}>
        <Typography variant='h5' fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
          {t('preContract.public.greeting', 'Ciao')} {info?.employee.firstName}!
        </Typography>
        <Typography variant='body2' color='text.secondary' mt={1} mb={3}>
          {t(
            'preContract.public.intro',
            'Leggi il tuo pre-contratto qui sotto, accetta le condizioni e firma. Tutto avviene online: niente da stampare.',
          )}
        </Typography>

        {/* PDF preview */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            mb: 3,
            height: { xs: 380, sm: 600, md: 720 },
          }}
        >
          <iframe
            src={previewUrl}
            title={t('preContract.public.headerTitle', 'Pre-contratto')}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        </Box>

        {/* Agreement */}
        <FormControlLabel
          control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
          label={
            <Typography variant='body2'>
              {t(
                'preContract.public.agreement',
                'Ho letto e accetto integralmente le condizioni del pre-contratto sopra riportato.',
              )}
            </Typography>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        {/* Name + signature */}
        <Stack gap={2}>
          <TextField
            label={t('preContract.public.fullName', 'Nome e cognome')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
          />

          <Box>
            <Stack direction='row' alignItems='center' gap={1} mb={1}>
              <EditIcon fontSize='small' color='action' />
              <Typography variant='body2' fontWeight={600}>
                {t('preContract.public.signHere', 'Firma qui sotto')}
              </Typography>
              <Button size='small' onClick={clearSignature} sx={{ ml: 'auto' }}>
                {t('preContract.public.clear', 'Cancella')}
              </Button>
            </Stack>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: hasSignature ? theme.palette.primary.main : 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                touchAction: 'none',
              }}
            >
              <canvas
                ref={canvasRef}
                width={800}
                height={220}
                style={{ width: '100%', height: 220, display: 'block', cursor: 'crosshair' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onPointerLeave={handlePointerUp}
              />
            </Box>
          </Box>

          {!agreed && (
            <Alert severity='info' variant='outlined'>
              {t(
                'preContract.public.mustAgree',
                'Per procedere devi accettare le condizioni e firmare.',
              )}
            </Alert>
          )}

          <Button
            variant='contained'
            size='large'
            onClick={() => { void handleSubmit(); }}
            disabled={!canSubmit}
            sx={{
              py: 1.5,
              bgcolor: '#0F172A',
              '&:hover': { bgcolor: '#1E293B' },
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color='inherit' />
            ) : (
              t('preContract.public.submit', 'Firma e invia')
            )}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default PreContractSignPage;
